/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-organization-security-dialog/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';

import { useBoolean, useTranslation } from 'hooks';
import { GET_SYSTEM_ORGANISATIONS, SAVE_ORGANISATION_SECURITY } from 'queries/org-admin';
import { GET_ORGANISATION } from 'queries/uam';
import { Authentication } from 'types/graphql-global-types';
import NotificationService from 'utils/notification';
import { validateFields } from 'utils/validation';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

import './styles.scss';

interface EditOrganizationConfDialogInterface {
    open: boolean;
    close(action?: string): any;
    currentOrganization: any;
}

const onlyNumbersPattern = /^[0-9]*$/;

const EditOrganizationSecurityDialog = ({ open, close, currentOrganization }: EditOrganizationConfDialogInterface) => {
    const { t } = useTranslation();
    const initialState = {
        twoFactorEnabled: false,
        config: null as any,
        recordId: '',
        created: '',
        updated: '',
        createdBy: '',
        updatedBy: '',
        minPasswordLength: 0,
        minLowercaseCount: 0,
        minUppercaseCount: 0,
        minSpecialCharCount: 0,
        minNumberCount: 0,
        numAttemptsBeforeLockout: 3,
        userIdleTimeoutMinutes: 10,
        accountInactiveDays: 0,
        lockoutPeriodMinutes: 10,
        authenticationType: Authentication.OPRA,
        singleSignOnIssuer: '',
    };
    const initialError = {
        accountInactiveDays: '',
        userIdleTimeoutMinutes: '',
        numAttemptsBeforeLockout: '',
        minPasswordLength: '',
        minLowercaseCount: '',
        minUppercaseCount: '',
        minSpecialCharCount: '',
        minNumberCount: '',
        lockoutPeriodMinutes: '',
    };
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialError });
    const disabledSaveButton = useBoolean(true);
    const [loadOrganization, { data }] = useLazyQuery(GET_ORGANISATION);
    const [saveOrganisationSecurity] = useMutation(SAVE_ORGANISATION_SECURITY);

    const isSso = state.authenticationType === Authentication.SSO;

    const onClose = () => {
        disabledSaveButton.setTrue();
        setState({ ...initialState });
        setErrors({ ...initialError });
        close();
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:organizationSuccessfulUpdated', { id: state.recordId }));
        onClose();
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const getAuthenticationTypeOptions = () => {
        const authenticationTypes = Object.values(Authentication) as Authentication[];
        return authenticationTypes.map((type: Authentication) => ({
            id: type,
            text: t(`admin:${type}`),
            active: true,
            default: type === Authentication.OPRA,
        }));
    };

    useEffect(() => {
        if (currentOrganization) {
            loadOrganization({ variables: { key: currentOrganization.key } });
        }
    }, [currentOrganization]);

    useEffect(() => {
        if (data?.organisation) {
            const fullOrganisation = data.organisation;
            setState({
                ...currentOrganization,
                minPasswordLength: fullOrganisation.security.minPasswordLength || 8,
                twoFactorEnabled: fullOrganisation.security.twoFactorEnabled || false,
                minLowercaseCount: fullOrganisation.security.minLowercaseCount || 0,
                minUppercaseCount: fullOrganisation.security.minUppercaseCount || 0,
                minSpecialCharCount: fullOrganisation.security.minSpecialCharCount || 0,
                numAttemptsBeforeLockout: fullOrganisation.security.numAttemptsBeforeLockout || 3,
                minNumberCount: fullOrganisation.security.minNumberCount || 0,
                userIdleTimeoutMinutes: fullOrganisation.security.userIdleTimeoutMinutes || 20,
                accountInactiveDays: fullOrganisation.security.accountInactiveDays || 90,
                lockoutPeriodMinutes: fullOrganisation.security.lockoutPeriodMinutes || 10,
                updated: fullOrganisation.updated,
                updatedBy: fullOrganisation.updatedBy,
                created: fullOrganisation.created,
                createdBy: fullOrganisation.createdBy,
                recordId: fullOrganisation.recordId,
                authenticationType: fullOrganisation.security.authenticationMethod || Authentication.OPRA,
                singleSignOnIssuer: fullOrganisation.security.ssoIssuer || '',
            });
        }
    }, [data]);

    const numberValidation = (value: number, fieldName: string, min: number, max: number) => {
        if (value > max || value < min)
            setErrors({
                ...errors,
                [fieldName]: 'Validation error',
            });
        else
            setErrors({
                ...errors,
                [fieldName]: '',
            });
    };

    const onChangeCheckbox = (field: string) => () => {
        if (disabledSaveButton.value) disabledSaveButton.setFalse();
        setState({ ...state, [field]: !(state as any)[field] });
    };

    const onChangeNumber = (fieldName: string, min?: number, max?: number) => (event: any) => {
        const { value } = event.target;

        if (!onlyNumbersPattern.test(value)) return;

        if (disabledSaveButton.value) disabledSaveButton.setFalse();
        setState({ ...state, [fieldName]: value });
        if (min !== undefined || max !== undefined) numberValidation(value, fieldName, min, max);
    };

    const onChangeText = (fieldName: string) => (event: any) => {
        const { value } = event.target;

        if (disabledSaveButton.value) disabledSaveButton.setFalse();

        setState({ ...state, [fieldName]: value });
    };

    const onChangeDropdown = (fieldName: string, value: any) => {
        if (disabledSaveButton.value) disabledSaveButton.setFalse();

        setState({ ...state, [fieldName]: value });
    };

    const checkRequiredFields = (fields: string[]) => {
        let isValid = true;
        let newErrors = { ...errors };
        fields.forEach((field) => {
            const error = validateFields((state as any)[field].toString(), field, 0, true);
            newErrors = {
                ...newErrors,
                ...error,
            };
        });
        if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) isValid = false;
        setErrors({ ...newErrors });
        return isValid;
    };

    const checkForErrors = () => Object.keys(errors).filter((item) => (errors as any)[item]).length > 0;

    const submit = () => {
        const requiredFields = isSso
            ? ['accountInactiveDays']
            : [
                  'minLowercaseCount',
                  'minUppercaseCount',
                  'minSpecialCharCount',
                  'minNumberCount',
                  'accountInactiveDays',
              ];

        if (!checkForErrors() && checkRequiredFields(requiredFields)) {
            disabledSaveButton.setTrue();
            saveOrganisationSecurity({
                variables: {
                    organisationKey: currentOrganization.key,
                    security: {
                        accountInactiveDays: +state.accountInactiveDays,
                        minLowercaseCount: +state.minLowercaseCount,
                        minNumberCount: +state.minNumberCount,
                        minPasswordLength: +state.minPasswordLength,
                        minSpecialCharCount: +state.minSpecialCharCount,
                        minUppercaseCount: +state.minUppercaseCount,
                        numAttemptsBeforeLockout: +state.numAttemptsBeforeLockout,
                        twoFactorEnabled: state.twoFactorEnabled,
                        userIdleTimeoutMinutes: +state.userIdleTimeoutMinutes,
                        lockoutPeriodMinutes: +state.lockoutPeriodMinutes,
                        authenticationMethod: state.authenticationType,
                        ssoIssuer: state.singleSignOnIssuer,
                    },
                    reason: '',
                },
                refetchQueries: [
                    { query: GET_SYSTEM_ORGANISATIONS },
                    {
                        query: GET_ORGANISATION,
                        variables: {
                            key: currentOrganization.key,
                        },
                    },
                ],
            })
                .then(() => {
                    successRequest();
                })
                .catch(() => {
                    errorRequest();
                });
        }
    };

    return (
        <>
            {open && (
                <CustomDialog
                    onClose={() => {
                        onClose();
                    }}
                    fullWidth
                    maxWidth="md"
                    scroll="paper"
                    open={open && data?.organisation}
                >
                    <DialogTitle id="edit-organization-security-modal-title">
                        {t('admin:editSecurityNumber', { id: state?.recordId })}
                        <FontAwesomeIcon
                            icon={['fal', 'times']}
                            className="fa-lg close-icon-cv"
                            onClick={() => {
                                onClose();
                            }}
                            data-testid="close-dialog"
                        />
                    </DialogTitle>
                    <DialogContent className="edit-organization-security-dialog-content">
                        <div className="value-list">
                            <p>
                                <span>{`${t('admin:organization')}: `}</span>
                                {data?.organisation?.name}
                            </p>
                        </div>
                        <div className="type-edc-wrapper">
                            <FormControl className="form-control-type not-full mr">
                                <SelectWithCategories
                                    onChange={(field: string) => (event: any) => {
                                        onChangeDropdown(field, event.target?.value);
                                    }}
                                    dataArray={getAuthenticationTypeOptions()}
                                    fieldSelect="authenticationType"
                                    labelField="authenticationType"
                                    customClassName="edit-organization-security-authentication-type"
                                    customId="edit-organization-security-authentication-type"
                                    customLabelId="edit-organization-security-authentication-type-label"
                                    showWarning={false}
                                    filterable={false}
                                    currentState={state.authenticationType}
                                    label={t('admin:authenticationType')}
                                />
                            </FormControl>
                            {!isSso && (
                                <FormControl>
                                    <Checkbox
                                        rounded={null}
                                        className="cell-checkbox no-border"
                                        checked={state.twoFactorEnabled}
                                        onChange={onChangeCheckbox('twoFactorEnabled')}
                                        label={t('admin:2FactorAuthentication')}
                                        data-testid="2-factor-authentication"
                                        size="medium"
                                    />
                                </FormControl>
                            )}
                        </div>
                        {!isSso && (
                            <OutlinedWrapper label={t('admin:passwordCharacters')}>
                                <div className="type-edc-wrapper">
                                    <FormControl variant="outlined" className="form-control-type not-full mr">
                                        <TextField
                                            label={`${t('admin:characterLength')}*`}
                                            id="edit-organization-security-dialog-minPasswordLength"
                                            fullWidth
                                            type="text"
                                            value={state.minPasswordLength}
                                            onChange={onChangeNumber('minPasswordLength', 8)}
                                            placeholder={t('admin:enterNumber')}
                                            helperText={
                                                <span
                                                    style={errors.minPasswordLength ? { color: '#ae0015' } : {}}
                                                    className="remaining-text"
                                                >
                                                    {t('admin:enterValueGreaterThan', { number: 7 })}
                                                </span>
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.minPasswordLength}
                                            className="before-select"
                                        />
                                    </FormControl>
                                </div>
                                <div className="type-edc-wrapper">
                                    <FormControl variant="outlined" className="form-control-type not-full mr">
                                        <TextField
                                            label={`${t('admin:numberOfLowercaseCharacters')}*`}
                                            id="edit-organization-security-dialog-minLowercaseCount"
                                            fullWidth
                                            type="text"
                                            value={state.minLowercaseCount}
                                            onChange={onChangeNumber('minLowercaseCount', 0)}
                                            placeholder={t('admin:enterNumber')}
                                            helperText={
                                                <span
                                                    style={errors.minLowercaseCount ? { color: '#ae0015' } : {}}
                                                    className="remaining-text"
                                                >
                                                    {errors.minLowercaseCount}
                                                </span>
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.minLowercaseCount}
                                            className="before-select"
                                        />
                                    </FormControl>
                                    <FormControl variant="outlined" className="form-control-type not-full">
                                        <TextField
                                            label={`${t('admin:numberOfUppercaseCharacters')}*`}
                                            id="edit-organization-security-dialog-minUppercaseCount"
                                            fullWidth
                                            type="text"
                                            value={state.minUppercaseCount}
                                            onChange={onChangeNumber('minUppercaseCount', 0)}
                                            placeholder={t('admin:enterNumber')}
                                            helperText={
                                                <span
                                                    style={errors.minUppercaseCount ? { color: '#ae0015' } : {}}
                                                    className="remaining-text"
                                                >
                                                    {errors.minUppercaseCount}
                                                </span>
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.minUppercaseCount}
                                            className="before-select"
                                        />
                                    </FormControl>
                                </div>
                                <div className="type-edc-wrapper">
                                    <FormControl variant="outlined" className="form-control-type not-full mr">
                                        <TextField
                                            label={`${t('admin:numberOfSpecialCharacters')}*`}
                                            id="edit-organization-security-dialog-minSpecialCharCount"
                                            fullWidth
                                            type="text"
                                            value={state.minSpecialCharCount}
                                            onChange={onChangeNumber('minSpecialCharCount', 0)}
                                            placeholder={t('admin:enterNumber')}
                                            helperText={
                                                <span
                                                    style={errors.minSpecialCharCount ? { color: '#ae0015' } : {}}
                                                    className="remaining-text"
                                                >
                                                    {errors.minSpecialCharCount}
                                                </span>
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.minSpecialCharCount}
                                            className="before-select"
                                        />
                                    </FormControl>
                                    <FormControl variant="outlined" className="form-control-type not-full">
                                        <TextField
                                            label={`${t('admin:numberOfNumbers')}*`}
                                            id="edit-organization-security-dialog-minNumberCount"
                                            fullWidth
                                            type="text"
                                            value={state.minNumberCount}
                                            onChange={onChangeNumber('minNumberCount', 0)}
                                            placeholder={t('admin:enterNumber')}
                                            helperText={
                                                <span
                                                    style={errors.minNumberCount ? { color: '#ae0015' } : {}}
                                                    className="remaining-text"
                                                >
                                                    {errors.minNumberCount}
                                                </span>
                                            }
                                            variant="outlined"
                                            color="secondary"
                                            error={!!errors.minNumberCount}
                                            className="before-select"
                                        />
                                    </FormControl>
                                </div>
                            </OutlinedWrapper>
                        )}
                        <OutlinedWrapper
                            customClass="login-security mb"
                            label={t('admin:loginSecurity')}
                            className="mb"
                        >
                            <div className="type-edc-wrapper">
                                <FormControl variant="outlined" className="form-control-type not-full mr">
                                    <TextField
                                        label={`${t('admin:failedLoginAttemptsBeforeLockout')}*`}
                                        id="edit-organization-security-dialog-numAttemptsBeforeLockout"
                                        fullWidth
                                        type="text"
                                        value={state.numAttemptsBeforeLockout}
                                        onChange={onChangeNumber('numAttemptsBeforeLockout', 2, 5)}
                                        placeholder={t('admin:enterNumber')}
                                        helperText={
                                            <span
                                                style={errors.numAttemptsBeforeLockout ? { color: '#ae0015' } : {}}
                                                className="remaining-text"
                                            >
                                                {t('admin:enterNumberBetween', { min: 2, max: 5 })}
                                            </span>
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        error={!!errors.numAttemptsBeforeLockout}
                                        className="before-select"
                                        disabled={isSso}
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type not-full">
                                    <TextField
                                        label={`${t('admin:lockoutPeriodMinutes')}*`}
                                        id="edit-organization-security-dialog-lockoutPeriodMinutes"
                                        fullWidth
                                        type="text"
                                        value={state.lockoutPeriodMinutes}
                                        onChange={onChangeNumber('lockoutPeriodMinutes', 10)}
                                        placeholder={t('admin:enterNumber')}
                                        helperText={
                                            <span
                                                style={errors.lockoutPeriodMinutes ? { color: '#ae0015' } : {}}
                                                className="remaining-text"
                                            >
                                                {t('admin:enterNumberGreaterThan', { number: 9 })}
                                            </span>
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        error={!!errors.lockoutPeriodMinutes}
                                        className="before-select"
                                    />
                                </FormControl>
                            </div>
                            <div className="type-edc-wrapper no-mb">
                                <FormControl variant="outlined" className="form-control-type not-full mr">
                                    <TextField
                                        label={`${t('admin:accountInactivityBeforeLockoutDays')}*`}
                                        id="edit-organization-security-dialog-accountInactiveDays"
                                        fullWidth
                                        type="text"
                                        value={state.accountInactiveDays}
                                        onChange={onChangeNumber('accountInactiveDays', 0)}
                                        placeholder={t('admin:enterNumber')}
                                        helperText={
                                            <span
                                                style={errors.accountInactiveDays ? { color: '#ae0015' } : {}}
                                                className="remaining-text"
                                            >
                                                {errors.accountInactiveDays}
                                            </span>
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        error={!!errors.accountInactiveDays}
                                        className="before-select"
                                    />
                                </FormControl>
                                <FormControl variant="outlined" className="form-control-type not-full">
                                    <TextField
                                        label={`${t('admin:logoutAfterIdlePeriodMinutes')}*`}
                                        id="edit-organization-security-dialog-logoutAfterIdlePeriodMinutes"
                                        fullWidth
                                        type="text"
                                        value={state.userIdleTimeoutMinutes}
                                        onChange={onChangeNumber('userIdleTimeoutMinutes', 5)}
                                        placeholder={t('admin:enterNumber')}
                                        helperText={
                                            <span
                                                style={errors.userIdleTimeoutMinutes ? { color: '#ae0015' } : {}}
                                                className="remaining-text"
                                            >
                                                {t('admin:enterNumberGreaterThan', { number: 4 })}
                                            </span>
                                        }
                                        variant="outlined"
                                        color="secondary"
                                        className={`before-select ${
                                            errors.userIdleTimeoutMinutes ? 'mb red-border' : 'mb'
                                        }`}
                                    />
                                </FormControl>
                            </div>
                        </OutlinedWrapper>
                        {isSso && (
                            <div className="type-edc-wrapper no-mb">
                                <FormControl className="form-control-type">
                                    <TextField
                                        label={`${t('admin:singleSignOnIssuer')}`}
                                        id="edit-organization-security-dialog-singleSignOnIssuer"
                                        fullWidth
                                        type="text"
                                        value={state.singleSignOnIssuer}
                                        onChange={onChangeText('singleSignOnIssuer')}
                                        placeholder={t('admin:enterSingleSignOnIssuer')}
                                        variant="outlined"
                                        color="secondary"
                                    />
                                </FormControl>
                            </div>
                        )}
                        <DialogActions
                            style={{ paddingBottom: 25, paddingTop: 25 }}
                            className="actions-info-wrapper-edit-organization-security flex-end"
                        >
                            <Button
                                disabled={
                                    disabledSaveButton.value ||
                                    Object.keys(errors).filter((item) => (errors as any)[item]).length > 0
                                }
                                className="edit-organization-security-btn"
                                text="update"
                                onClick={submit}
                            />
                        </DialogActions>
                    </DialogContent>
                </CustomDialog>
            )}
        </>
    );
};

export default EditOrganizationSecurityDialog;
